import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BrandMark } from "../design/BrandMark";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { homePathFor } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { Message } from "./Message";
import { SURFACE_PREFIX } from "../system/navigation";
import { holdHubGrant } from "./hubGrant";

// ---------------------------------------------------------------------------
// The three screens an emailed link lands on: /enter, /reset, /verify.
//
// THE TOKEN IS TAKEN OUT OF THE ADDRESS BAR IMMEDIATELY.
//
// web/src/api/auth.ts states the rule these screens have to bend: every secret
// travels in the body, never in the URL, because a query string is written to
// browser history, to every proxy log, and to the Referer of the next request
// the page makes. An emailed link cannot obey that — the token has to arrive
// somehow — so the exception is contained instead: the value is read once into
// memory and the history entry is replaced with a clean path before anything
// else renders. What remains in the address bar, in history, and in any Referer
// this page later sends is /reset with no token on it.
//
// The token is still in the mailbox, which is why V3 makes every one of them
// single-use and short-lived at the database.
// ---------------------------------------------------------------------------

type Purpose = "enter" | "reset" | "verify";

function useOneShotToken(): string | null {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const captured = useRef<string | null>(null);
  if (captured.current === null) captured.current = params.get("token");

  useEffect(() => {
    if (params.get("token") !== null) {
      navigate(location.pathname, { replace: true });
    }
  }, [params, navigate]);

  return captured.current;
}

/**
 * /enter?token= — the worker's sign-in link (§6.5). S63: worded in the browser's
 * language, because nobody is known until the link is consumed.
 */
export function EnterWithLink() {
  return (
    <PreSessionLocale>
      <EnterWithLinkScreen />
    </PreSessionLocale>
  );
}

function EnterWithLinkScreen() {
  // V-ETA-2b. The same path carries event.clinic's request link, ?grant_token=, which the
  // hub cannot send anywhere else (its email names <entry_origin>/enter).
  const [params] = useSearchParams();
  if (params.get("grant_token") !== null) return <HubGrantLanding />;
  return <MagicLinkScreen />;
}

/**
 * /enter?grant_token=egt_… — an event.clinic staffing request. The grant is read once into
 * memory, held in this tab (hubGrant.ts), the history entry is replaced before anything
 * else renders, and the console's requests screen spends it. Signed out, the console
 * sends the person to sign in, and every sign-in path returns to the waiting request.
 */
function HubGrantLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const captured = useRef<string | null>(null);
  if (captured.current === null) captured.current = params.get("grant_token");
  useEffect(() => {
    // An empty grant, or a browser that will not keep it, is said on the requests screen
    // rather than landing there with nothing (audit of Runs 92-96, finding 4).
    const held = captured.current ? holdHubGrant(captured.current) : false;
    navigate(`${SURFACE_PREFIX.console}/hub-requests`, { replace: true, state: held ? null : { hubLinkLost: true } });
  }, [navigate]);
  return null;
}

function MagicLinkScreen() {
  const { t } = useT();
  const token = useOneShotToken();
  const { apiBase, reload } = useSession();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      if (!token) {
        setFailed(true);
        return;
      }
      try {
        const { status } = await send(requests.consumeMagicLink(apiBase, token));
        if (status !== 200) {
          setFailed(true);
          return;
        }
        const me = await reload();
        navigate((me && homePathFor(me)) ?? SURFACE_PREFIX.worker, { replace: true });
      } catch {
        // Without this the screen reads "Signing you in…" for ever. Saying the
        // link failed is imperfect — the link may be fine and the network not —
        // but a dead end with no words is worse than an imprecise one with a
        // way out, and the way out is to ask for another link.
        setFailed(true);
      }
    })();
  }, [token, apiBase, reload, navigate]);

  if (failed) {
    return (
      <Message
        tone="failed"
        status={t("Link not valid")}
        title={t("That sign-in link has expired")}
        action={{ label: t("Go to sign in"), to: "/sign-in" }}
      >
        <p>
          {t("Sign-in links last a short time and work once. Ask for a new one and use the most recent email.")}
        </p>
      </Message>
    );
  }
  return <Message tone="neutral" status={t("Working")} title={t("Signing you in…")} />;
}

/** /verify?token= — confirming an email address. S75: the browser's language. */
export function VerifyEmail() {
  return (
    <PreSessionLocale>
      <VerifyEmailScreen />
    </PreSessionLocale>
  );
}

function VerifyEmailScreen() {
  const { t } = useT();
  const token = useOneShotToken();
  const { apiBase } = useSession();
  const [outcome, setOutcome] = useState<"working" | "done" | "failed">("working");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      if (!token) {
        setOutcome("failed");
        return;
      }
      try {
        const { status } = await send(requests.confirmEmailVerification(apiBase, token));
        setOutcome(status === 200 ? "done" : "failed");
      } catch {
        setOutcome("failed");
      }
    })();
  }, [token, apiBase]);

  if (outcome === "working") return <Message tone="neutral" status={t("Working")} title={t("Checking that link…")} />;
  if (outcome === "failed") {
    return (
      <Message
        tone="failed"
        status={t("Link not valid")}
        title={t("That link has expired")}
        action={{ label: t("Go to sign in"), to: "/sign-in" }}
      />
    );
  }
  return (
    <Message
      tone="live"
      status={t("Confirmed")}
      title={t("Email address confirmed")}
      action={{ label: t("Continue"), to: "/sign-in" }}
    />
  );
}

/** /reset?token= — choosing a new password (§6.3). S75: the browser's language. */
export function ResetPassword() {
  return (
    <PreSessionLocale>
      <ResetPasswordScreen />
    </PreSessionLocale>
  );
}

function ResetPasswordScreen() {
  const { t, refusal } = useT();
  const token = useOneShotToken();
  const { apiBase } = useSession();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !token) return;
    setBusy(true);
    setError(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(
        requests.confirmPasswordReset(apiBase, token, password),
      );
      if (status === 200) {
        setDone(true);
        return;
      }
      // The server's own words. It knows which rule was broken and this screen
      // does not — restating a password policy here would be a second copy of
      // it, and the two would part company the first time the real one changed.
      setError(refusal(body.error) ?? t("That did not work. Ask for a new link and try again."));
    } catch {
      // A NETWORK FAILURE MUST STILL SAY SOMETHING.
      //
      // Found by watching this exact screen: without the catch, a failed fetch
      // left the button reading "Working…" for ever, with no message and no way
      // back. The person cannot tell a slow server from a dead one from a
      // password they typed wrong, and the only remaining move is to reload and
      // lose the link.
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      // In `finally`, not after the await, so an early return cannot leave the
      // form disabled — which is the same defect wearing a different hat.
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <Message
        tone="failed"
        status={t("Link not valid")}
        title={t("That link is incomplete")}
        action={{ label: t("Go to sign in"), to: "/sign-in" }}
      />
    );
  }

  if (done) {
    return (
      <Message
        tone="live"
        status={t("Password set")}
        title={t("Your password is set")}
        action={{ label: t("Sign in"), to: "/sign-in" }}
      >
        <p>
          {t("Every other session was signed out, on every device. If that was not you, sign in and set another password now.")}
        </p>
      </Message>
    );
  }

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        <h1>{t("Set a new password")}</h1>
        <div className="form-message" role="status" aria-live="polite">
          {error ? (
            <>
              <StatusChip tone="failed" label={t("Not set")} />
              <span>{error}</span>
            </>
          ) : null}
        </div>
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="reset-password">{t("New password")}</label>
            <input
              id="reset-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="field-hint">
              {t("Long is stronger than complicated. A phrase you will remember beats a short password full of symbols.")}
            </span>
          </div>
          <button className="btn btn-block" type="submit" disabled={busy}>
            {busy ? t("Working…") : t("Set password")}
          </button>
        </form>
      </div>
    </main>
  );
}

export const TOKEN_LINK_PURPOSES: readonly Purpose[] = ["enter", "reset", "verify"] as const;

/**
 * /unsubscribe?token= — SWEEP S7, the link at the foot of every notification email.
 *
 * NOTHING HAPPENS ON ARRIVAL. Mail scanners and link previewers open every link in a
 * message; a page that unsubscribed on load would unsubscribe people who never clicked.
 * So the token is held and the button sends it.
 *
 * AND THE ANSWER IS THE SAME WHATEVER THE SERVER FOUND, because the route answers a
 * constant on purpose (V25): a page that said "that link was not valid" would make it the
 * oracle the route refuses to be.
 */
export function Unsubscribe() {
  return (
    <PreSessionLocale>
      <UnsubscribeScreen />
    </PreSessionLocale>
  );
}

function UnsubscribeScreen() {
  const { t } = useT();
  const token = useOneShotToken();
  const { apiBase } = useSession();
  const [state, setState] = useState<"ready" | "working" | "done" | "unreachable">("ready");

  async function stop() {
    setState("working");
    try {
      await send(requests.unsubscribe(apiBase, token ?? ""));
      setState("done");
    } catch {
      setState("unreachable");
    }
  }

  if (state === "done") {
    return (
      <Message tone="live" status={t("Done")} title={t("You will not get these emails any more")}>
        {/* WHAT app.unsubscribe_all_notifications DOES, AND NO MORE: every kind, every
            channel, in every organisation this person belongs to. */}
        <p>{t("Every notification from every organisation you work with on Verbunk is switched off.")}</p>
      </Message>
    );
  }
  return (
    <Message
      tone={state === "unreachable" ? "failed" : "neutral"}
      status={state === "unreachable" ? t("Not sent") : t("One step")}
      title={t("Stop emails from Verbunk")}
    >
      {state === "unreachable" ? (
        <p>{t("We could not reach Verbunk. Check your connection and press the button again.")}</p>
      ) : (
        <p>{t("Press the button to stop the emails this link came in.")}</p>
      )}
      <button type="button" className="btn" disabled={state === "working"} onClick={() => void stop()}>
        {state === "working" ? t("Working…") : t("Stop these emails")}
      </button>
    </Message>
  );
}
